import type { Dispatch, SetStateAction } from "react";
import AdminSection from "../../../shared/components/AdminSection";
import Button from "../../../shared/components/Button";
import ImageUploadField from "../../../shared/components/ImageUploadField";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { ChallengeHistoryItem } from "../../challenges/types/challengeAdmin";
import type { SiteContent } from "../../home/shared/types/siteContent";
import type { AdminEventChallengeLink } from "../services/eventRegistrationAdminService";
import type { ContentTab } from "../types/admin";
import AdminContentEventsPanel from "./AdminContentEventsPanel";
import {
  AdminContentAboutPanel,
  AdminContentHeroPanel,
} from "./AdminContentIntroPanels";
import AdminContentToolbar from "./AdminContentToolbar";

type ContentListSection = "highlights" | "events" | "products" | "instagram";

type AdminContentPanelProps = {
  addListItem: (section: ContentListSection) => void;
  contentDraft: SiteContent;
  contentTab: ContentTab;
  eventChallengeLinkDrafts: Record<string, string>;
  eventChallengeLinks: Record<string, AdminEventChallengeLink>;
  eventChallengeLinksLoading: boolean;
  linkableEventChallengesSorted: ChallengeHistoryItem[];
  onDeleteEventLink: (eventId: string) => Promise<void>;
  onUpsertEventLink: (eventId: string) => Promise<void>;
  removeHeroImage: () => Promise<void>;
  removeListImage: (section: ContentListSection, index: number) => Promise<void>;
  removeListItem: (section: ContentListSection, index: number) => void;
  saveContent: () => Promise<void>;
  savingContent: boolean;
  setContentTab: Dispatch<SetStateAction<ContentTab>>;
  setEventChallengeLinkDrafts: Dispatch<SetStateAction<Record<string, string>>>;
  updateAbout: (field: string, value: string | string[]) => void;
  updateHero: (field: string, value: string) => void;
  updateListItem: (
    section: ContentListSection,
    index: number,
    field: string,
    value: string,
  ) => void;
};

export default function AdminContentPanel({
  addListItem,
  contentDraft,
  contentTab,
  eventChallengeLinkDrafts,
  eventChallengeLinks,
  eventChallengeLinksLoading,
  linkableEventChallengesSorted,
  onDeleteEventLink,
  onUpsertEventLink,
  removeHeroImage,
  removeListImage,
  removeListItem,
  saveContent,
  savingContent,
  setContentTab,
  setEventChallengeLinkDrafts,
  updateAbout,
  updateHero,
  updateListItem,
}: AdminContentPanelProps) {
  const t = siteTexts.admin;

  return (
    <div className="admin-content">
      <AdminContentToolbar
        contentTab={contentTab}
        onSave={saveContent}
        saving={savingContent}
        setContentTab={setContentTab}
      />

      {contentTab === "hero" && (
        <AdminContentHeroPanel
          contentDraft={contentDraft}
          removeHeroImage={removeHeroImage}
          updateHero={updateHero}
        />
      )}

      {contentTab === "about" && (
        <AdminContentAboutPanel
          contentDraft={contentDraft}
          updateAbout={updateAbout}
        />
      )}

      {contentTab === "highlights" && (
        <AdminSection
          title={t.sections.highlights}
          action={
            <Button
              type="button"
              variant="pill"
              onClick={() => addListItem("highlights")}
            >
              {t.actions.addHighlight}
            </Button>
          }
        >
          <div className="admin-list">
            {contentDraft.highlights.map((item, index) => (
              <div key={`highlight-${index}`} className="admin-card">
                <label className="field">
                  <span>{t.labels.title}</span>
                  <input
                    type="text"
                    value={item.title}
                    onChange={(event) =>
                      updateListItem(
                        "highlights",
                        index,
                        "title",
                        event.target.value,
                      )
                    }
                  />
                </label>
                <label className="field">
                  <span>{t.labels.description}</span>
                  <textarea
                    value={item.description}
                    onChange={(event) =>
                      updateListItem(
                        "highlights",
                        index,
                        "description",
                        event.target.value,
                      )
                    }
                    rows={3}
                  />
                </label>
                <ImageUploadField
                  label={t.labels.image}
                  value={item.imageUrl}
                  onChange={(nextValue) =>
                    updateListItem("highlights", index, "imageUrl", nextValue)
                  }
                  onRemove={() => removeListImage("highlights", index)}
                />
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => removeListItem("highlights", index)}
                >
                  {siteTexts.common.remove}
                </Button>
              </div>
            ))}
          </div>
        </AdminSection>
      )}

      {contentTab === "events" && (
        <AdminContentEventsPanel
          addListItem={addListItem}
          contentDraft={contentDraft}
          eventChallengeLinkDrafts={eventChallengeLinkDrafts}
          eventChallengeLinks={eventChallengeLinks}
          eventChallengeLinksLoading={eventChallengeLinksLoading}
          linkableEventChallengesSorted={linkableEventChallengesSorted}
          onDeleteEventLink={onDeleteEventLink}
          onUpsertEventLink={onUpsertEventLink}
          removeListImage={removeListImage}
          removeListItem={removeListItem}
          setEventChallengeLinkDrafts={setEventChallengeLinkDrafts}
          updateListItem={updateListItem}
        />
      )}

      {contentTab === "products" && (
        <AdminSection
          title={t.sections.products}
          action={
            <Button
              type="button"
              variant="pill"
              onClick={() => addListItem("products")}
            >
              {t.actions.addProduct}
            </Button>
          }
        >
          <div className="admin-list">
            {contentDraft.products.map((item, index) => (
              <div key={`product-${index}`} className="admin-card">
                <label className="field">
                  <span>{t.labels.title}</span>
                  <input
                    type="text"
                    value={item.title}
                    onChange={(event) =>
                      updateListItem(
                        "products",
                        index,
                        "title",
                        event.target.value,
                      )
                    }
                  />
                </label>
                <label className="field">
                  <span>{t.labels.price}</span>
                  <input
                    type="text"
                    value={item.price}
                    onChange={(event) =>
                      updateListItem(
                        "products",
                        index,
                        "price",
                        event.target.value,
                      )
                    }
                  />
                </label>
                <label className="field">
                  <span>{t.labels.description}</span>
                  <textarea
                    value={item.description}
                    onChange={(event) =>
                      updateListItem(
                        "products",
                        index,
                        "description",
                        event.target.value,
                      )
                    }
                    rows={3}
                  />
                </label>
                <ImageUploadField
                  label={t.labels.image}
                  value={item.imageUrl}
                  onChange={(nextValue) =>
                    updateListItem("products", index, "imageUrl", nextValue)
                  }
                  onRemove={() => removeListImage("products", index)}
                />
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => removeListItem("products", index)}
                >
                  {siteTexts.common.remove}
                </Button>
              </div>
            ))}
          </div>
        </AdminSection>
      )}

      {contentTab === "instagram" && (
        <AdminSection
          title={t.sections.instagram}
          action={
            <Button
              type="button"
              variant="pill"
              onClick={() => addListItem("instagram")}
            >
              {t.actions.addPost}
            </Button>
          }
        >
          <div className="admin-list">
            {contentDraft.instagram.map((item, index) => (
              <div key={`instagram-${index}`} className="admin-card">
                <label className="field">
                  <span>{t.labels.link}</span>
                  <input
                    type="url"
                    value={item.link}
                    onChange={(event) =>
                      updateListItem(
                        "instagram",
                        index,
                        "link",
                        event.target.value,
                      )
                    }
                  />
                </label>
                <ImageUploadField
                  label={t.labels.image}
                  value={item.imageUrl}
                  onChange={(nextValue) =>
                    updateListItem("instagram", index, "imageUrl", nextValue)
                  }
                  onRemove={() => removeListImage("instagram", index)}
                />
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => removeListItem("instagram", index)}
                >
                  {siteTexts.common.remove}
                </Button>
              </div>
            ))}
          </div>
        </AdminSection>
      )}
    </div>
  );
}
